import React, {useEffect, useRef, useState} from "react";
import 'material-design-inspired-color-picker';
import {createUUId} from "../../../../core/services/utils/utils";
import {Popover} from "@mui/material";

const ColorPickerPopover = ({children, color, onSelect, onImmediateChange, className = '', anchorOrigin, transformOrigin}) => {
    const id = useRef(`_${createUUId()}`)
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedColor, setSelectedColor] = useState(color)

    /**
     * Sets a click event listener that closes the popover whenever user clicks outside of it
     */
    useEffect(() => {
        window.addEventListener('click', stopPropagation)
        return () => {
            window.removeEventListener('click', stopPropagation)
        }
    }, [])

    /**
     * Listens for the changes in color and with each change:
     * sets the selected color of the
     */
    useEffect(() => {
        setSelectedColor(color)
    }, [color])

    /**
     * Listens for the changes in selectedColor and with each change:
     * invokes the onImmediateChange callback with the selected color to reflect the immediate change
     */
    useEffect(() => {
        if (onImmediateChange) onImmediateChange(selectedColor)
    }, [selectedColor])

    /**
     * Listens for the changes in the popover open state and with each change:
     *
     * if the popover is opened, then sets the onChange handler for the picker to reflect the changes of the picker
     * if the popover is closed, calls the onselect callback with the final color selection.
     */
    useEffect(() => {
        if (anchorEl === null) {
            if (!selectedColor) return
            if (onSelect) onSelect(selectedColor)
            return
        }
        let picker;
        const handler = (e) => onColorChanged(e, picker);
        setTimeout(() => {
            picker = document.getElementById(id.current)
            if (!picker) return;
            picker.addEventListener('change', handler)
            picker.addEventListener('click', stopPropagation)
        }, 500)
        return () => {
            picker?.removeEventListener('change', handler)
            picker?.removeEventListener('click', stopPropagation)
        };
    }, [anchorEl])

    /**
     * Stops the propagation of this event to prevent some bugs regarding tab selection.
     * @param {Event} e
     */
    const stopPropagation = (e) => {
        e.stopPropagation()
    }

    /**
     * Changes the color of the picker along with setting the inner state.
     * @param {MouseEvent} e
     * @param {any} picker
     */
    const onColorChanged = (e, picker) => {
        const newColor = e.detail[0]
        picker.value = newColor
        const backButton = picker.getElementsByClassName('back-icon')
        if (backButton.length) {
            backButton[0].addEventListener('click', stopPropagation)
        }
        setSelectedColor(newColor)
    }

    /**
     * Closes the popover
     */
    const onLeave = () => setAnchorEl(null);

    /**
     * opens the popover
     */
    const onEnter = (event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    }

    return (
        <>
            <div
                className={className}
                aria-owns={!!anchorEl ? id : undefined}
                onClick={stopPropagation}
            >
                {children(onEnter)}
            </div>
            <Popover
                id={id}
                open={!!anchorEl}
                anchorEl={anchorEl}
                onClick={stopPropagation}
                classes={{
                    paper: 'p-4'
                }}
                anchorOrigin={anchorOrigin ?? {vertical: 'top', horizontal: 'right',}}
                transformOrigin={transformOrigin ?? {vertical: 'bottom', horizontal: 'left',}}
                onClose={onLeave}
                disableRestoreFocus
            >
                <md-color-picker
                    value={selectedColor ?? '#ffffff00'}
                    id={id.current}
                    palette={"material-full"}
                    color-margin={5}
                    colors-per-row={8}
                    color-size={40}
                    default-tint={300}
                    use-spectrum-picker={true}
                />
            </Popover>
        </>
    )
}

export default ColorPickerPopover;
